import { Query } from 'apollo-angular';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter, TemplateRef, ViewChild } from '@angular/core';
import { DataMapper, PaginatedConnectionParams, TableDataSource } from './table-data-source';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';
import { TableLazyLoadEvent, TableSelectAllChangeEvent } from 'primeng/table';
import { FilterDefinition } from './filter-panel/filter-panel.component';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';

export type PaginatedTableCellType = 'date' | 'boolean'

// String values that should be passed in already localized (use $localize at call sites)
export type LocalizedString = string;

export interface ColumnDefinition<OrderKey = string> {
  attr: string;
  label: LocalizedString;
  orderKey?: OrderKey;
  visible?: boolean;
  width?: number;
  cellType?: PaginatedTableCellType;
  template?: () => TemplateRef<any>;
}

class Column {
  columnDef: ColumnDefinition
  visible: boolean

  constructor(columnDef: ColumnDefinition) {
    this.columnDef = columnDef
    this.visible = columnDef.visible == undefined ? true : columnDef.visible
  }
}

type RowGroupMode = undefined | 'subheader' | 'rowspan'

@Component({
  selector: 'app-paginated-table',
  templateUrl: './paginated-table.component.html',
  styleUrls: ['./paginated-table.component.scss'],
  standalone: false
})
export class PaginatedTableComponent<QueryData, RowItem, OrderKey extends string, FilterData> implements OnInit {
  @Output() rowClick = new EventEmitter();
  @Input("columns") columnDefs!: ColumnDefinition[];
  @Input("filters") filterDefs: FilterDefinition[] | null = null;
  @Input() pageSize = 25;
  @Input() pageSizeOptions: number[] = [5, 25, 50, 100, 200, 500];
  @Input({ required: true }) query!: Query<QueryData, PaginatedConnectionParams<OrderKey, FilterData>>;
  @Input({ required: true }) dataMapper!: DataMapper<QueryData, RowItem>;
  @Input() rowGroupMode: RowGroupMode = 'subheader';
  @Input() groupRowsBy: string | undefined = undefined;
  @Input() rowMenuItems: MenuItem[] | undefined = undefined;
  @Input() orderKey: OrderKey | null = null
  @Input() orderAsc: boolean = true
  dataSource!: TableDataSource<QueryData, RowItem, OrderKey, FilterData>;
  value!: RowItem[];
  totalItemCount = 0;
  isLoading = true;
  private pageIndex = 1;
  protected columns: Column[] = []
  totalRecords!: number;
  selection: RowItem[] = [];
  selectAll: boolean = false;
  filterQueryParameter: FilterData | null = null;
  currentRowMenuItem?: RowItem; // For row menu actions

  @Output() selectionChange = new EventEmitter<RowItem[]>();
  @Output() selectAllChange = new EventEmitter<boolean>();
  @Output() orderByChange = new EventEmitter<{key: OrderKey, asc: boolean} | null>();
  @Output() filterQueryParameterChange = new EventEmitter<FilterData | null>();

  tableSettingsDialogVisible = false

  @ViewChild('dateCellTemplate', { read: TemplateRef }) dateCellTemplate!: TemplateRef<any>;
  @ViewChild('booleanCellTemplate', { read: TemplateRef }) booleanCellTemplate!: TemplateRef<any>;
  @ViewChild('rowMenu') rowMenu!: Menu;

  /**
   * Deep access in objects
   *
   * @example
   * // Returns { d: 4 }
   * let myHash = { a: { b: { d: 4 }, c: 3 } };
   * dig(myHash, 'a.b');
   *
   * // Returns 4
   * dig(myHash, 'a.b.d');
   */
  dig(obj: any, path: string): any {
    return obj && path.split('.').reduce(
      (result, attr) => result == null ? undefined : result[attr], obj
    );
  }

  ngOnInit() {
    this.columns = this.columnDefs.map(col => new Column(col))

    this.dataSource = new TableDataSource(this.query, this.dataMapper);
    this.dataSource.dataSubject$.subscribe(rowItems => {
      this.value = rowItems;
      if(this.selectAll) {
        this.selection = [...this.value];
      }
    })
    this.dataSource.totalCount$.subscribe(count => this.totalItemCount = count);
    this.dataSource.loading$.subscribe(isLoading => this.isLoading = isLoading);
    this.loadData(true)
  }

  isRowClickBound(): boolean {
    return this.rowClick.observers.length > 0;
  }

  onRowClick(row: RowItem) {
    this.rowClick.emit(row);
  }

  loadData(count = false) {
    this.dataSource.loadData(this.pageIndex, this.pageSize, this.orderKey, this.orderAsc, count, this.filterQueryParameter);
  }

  onSettingsIconClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()

    this.tableSettingsDialogVisible = true
  }

  onChangeColumnDisplayed(column: Column) {
    column.visible = !column.visible
  }

  getTemplate(column: ColumnDefinition): TemplateRef<any> | null {
    if(column.cellType) {
      switch(column.cellType) {
        case 'date': { return this.dateCellTemplate }
        case 'boolean': { return this.booleanCellTemplate }
      }
    } else if (column.template) {
      column.template()
    }
    return null
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    this.pageIndex = (event.first! / event.rows!) + 1
    this.pageSize = event.rows!
    this.setOrderBy(event.sortField as OrderKey | null, event.sortOrder == 1)
    this.loadData()
  }

  protected setOrderBy(key: OrderKey | null, asc: boolean) {
    if (this.orderKey != key || this.orderAsc != asc) {
      this.orderKey = key
      this.orderAsc = asc
      this.orderByChange.emit(this.orderKey == null ? null : { key: this.orderKey, asc: this.orderAsc });
    }
  }

  getVisibleColumns() {
    return this.columns.filter(c => c.visible)
  }

  onSelectionChange(value = []) {
    this.selectAll = value.length === this.totalRecords;
    this.selection = value;
    this.selectionChange.emit(this.selection);
    this.selectAllChange.emit(this.selectAll);
  }

  onSelectAllChange(event: TableSelectAllChangeEvent) {
    const checked = event.checked;

    if (checked) {
      this.selectAll = true;
      this.selection = [...this.value];
      this.selectionChange.emit(this.selection);
    } else {
      this.selection = [];
      this.selectionChange.emit(this.selection);
      this.selectAll = false;
    }
    this.selectAllChange.emit(this.selectAll);
  }

  onFilterQueryParameterChange(filterQueryParameter: FilterData | null) {
    this.filterQueryParameter = filterQueryParameter;
    this.filterQueryParameterChange.emit(this.filterQueryParameter);
    this.selection = [];
    this.selectionChange.emit(this.selection);
    this.loadData(true);
  }

  showRowMenu(item: RowItem, event: MouseEvent) {
    this.currentRowMenuItem = item; // Store the current item for use in menu actions
    this.rowMenu.toggle(event);
  }
}
